'use client';

import { c as _c } from "react/compiler-runtime";
import { useMemo, useState } from 'react';
import { History } from 'lucide-react';
import { cn } from '~/utils';
import { useI18n } from '~/features/i18n';
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover';
import { RecentChatsList } from './RecentChatsList';
import { useActivityAiChatStore } from './activity-ai-chat-store';

/**
 * The chat-history dropdown rendered at the top-right of the AI search bar. Shared by the
 * /teambuildings composer and the homepage hero so both expose the same "Previous chats"
 * affordance. The caller decides what selecting a thread does (open it in place vs navigate).
 */
export const AiChatHistoryButton: React.FC<{
  activeThreadId?: string;
  onSelect: (id: string) => void;
}> = t0 => {
  const $ = _c(24);
  const {
    activeThreadId,
    onSelect
  } = t0;
  const i18n = useI18n();
  const threads = useActivityAiChatStore(_temp);
  let t1;
  if ($[0] !== threads) {
    t1 = Object.values(threads).sort(_temp2);
    $[0] = threads;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const sortedThreads = t1;
  const [open, setOpen] = useState(false);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = cn("flex size-9 shrink-0 items-center justify-center rounded-full text-stone-500 transition-colors", "hover:bg-stone-100 hover:text-stone-800", "data-[state=open]:bg-stone-100 data-[state=open]:text-stone-800");
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== i18n) {
    t3 = i18n._("activity.search.ai.historyTrigger", undefined, {
      message: "Previous chats"
    });
    $[3] = i18n;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <History className="size-4" strokeWidth={2} aria-hidden={true} />;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t3) {
    t5 = <PopoverTrigger asChild={true}><button type="button" className={t2} aria-label={t3}>{t4}</button></PopoverTrigger>;
    $[6] = t3;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== i18n) {
    t6 = i18n._("activity.search.ai.historyHeading", undefined, {
      message: "Recent chats"
    });
    $[8] = i18n;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t6) {
    t7 = <div className="px-3 py-2"><p className="text-xs font-semibold text-foreground">{t6}</p></div>;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== activeThreadId || $[13] !== i18n || $[14] !== onSelect || $[15] !== sortedThreads) {
    t8 = sortedThreads.length === 0 ? <p className="px-3 py-4 text-center text-xs text-muted-foreground">{i18n._("activity.search.ai.historyPreviewNotice", undefined, {
        message: "Preview only \u2014 history is not saved yet."
      })}</p> : <RecentChatsList threads={sortedThreads} activeThreadId={activeThreadId} showSearch={true} variant="popover" className="px-3 py-2" onSelect={id => {
      setOpen(false);
      onSelect(id);
    }} />;
    $[12] = activeThreadId;
    $[13] = i18n;
    $[14] = onSelect;
    $[15] = sortedThreads;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t7 || $[18] !== t8) {
    t9 = <PopoverContent align="end" sideOffset={12} className="w-[min(100vw-2rem,22rem)] p-0 shadow-lg">{t7}{t8}</PopoverContent>;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== open || $[21] !== t5 || $[22] !== t9) {
    t10 = <Popover open={open} onOpenChange={setOpen}>{t5}{t9}</Popover>;
    $[20] = open;
    $[21] = t5;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  return t10;
};
function _temp(s) {
  return s.threads;
}
function _temp2(a, b) {
  return b.createdAt - a.createdAt;
}